// @ts-nocheck
/**
 * Audit Log Dashboard Component
 * Epic #17: RBAC & Permissions Platform Services
 *
 * Comprehensive audit log viewing with filtering, pagination, and CSV export
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Column,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Tag,
  Button,
  Pagination,
  DatePicker,
  DatePickerInput,
  MultiSelect,
  Loading,
  InlineNotification,
  Modal,
  Tile,
} from '@carbon/react';
import { Download, View, Search, Filter } from '@carbon/icons-react';
import axios from 'axios';
import { useAuthStore } from '../../store/authStore';
import styles from './AuditLogDashboard.module.scss';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

interface AuditLog {
  audit_id: number;
  event_type: string;
  user_id: string;
  user_email: string;
  resource_type: string;
  resource_id?: string;
  action: string;
  result: 'granted' | 'denied';
  timestamp: string;
}

interface AuditLogDetail extends AuditLog {
  user: {
    user_id: string;
    email: string;
    first_name?: string;
    last_name?: string;
    role?: string;
  };
  resource: {
    type: string;
    id?: string;
  };
  decision: {
    result: 'granted' | 'denied';
    basis?: any;
  };
  compliance?: any;
  metadata: {
    ip_address?: string;
    user_agent?: string;
    timestamp: string;
  };
}

const AuditLogDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, token } = useAuthStore();

  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [totalLogs, setTotalLogs] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>([]);
  const [selectedResults, setSelectedResults] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);

  // Modal state
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AuditLogDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Export state
  const [exporting, setExporting] = useState(false);

  // Event type options
  const eventTypeOptions = [
    { id: 'permission_checked', label: 'Permission Checked' },
    { id: 'permission_granted', label: 'Permission Granted' },
    { id: 'permission_denied', label: 'Permission Denied' },
    { id: 'role_assigned', label: 'Role Assigned' },
    { id: 'role_revoked', label: 'Role Revoked' },
    { id: 'permission_modified', label: 'Permission Modified' },
  ];

  // Result options
  const resultOptions = [
    { id: 'granted', label: 'Granted' },
    { id: 'denied', label: 'Denied' },
  ];

  // Fetch audit logs
  const fetchAuditLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query parameters
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('per_page', pageSize.toString());

      if (searchTerm) {
        params.append('user_id', searchTerm);
      }

      if (selectedEventTypes.length > 0) {
        params.append('event_type', selectedEventTypes[0]); // API supports single event type
      }

      if (selectedResults.length > 0) {
        params.append('result', selectedResults[0]); // API supports single result
      }

      if (dateRange[0]) {
        params.append('start_date', dateRange[0].toISOString());
      }

      if (dateRange[1]) {
        params.append('end_date', dateRange[1].toISOString());
      }

      const response = await axios.get(`${API_BASE_URL}/v1/admin/rbac/audit-logs?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setLogs(response.data.data.logs);
        setTotalLogs(response.data.data.pagination.total);
      } else {
        setError(response.data.message || 'Failed to load audit logs');
      }
    } catch (err: any) {
      console.error('Failed to fetch audit logs:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [token, currentPage, pageSize, searchTerm, selectedEventTypes, selectedResults, dateRange]);

  // Fetch audit log details
  const fetchAuditLogDetail = async (auditId: number) => {
    try {
      setLoadingDetail(true);

      const response = await axios.get(`${API_BASE_URL}/v1/admin/rbac/audit-logs/${auditId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setSelectedLog(response.data.data);
        setIsDetailModalOpen(true);
      }
    } catch (err: any) {
      console.error('Failed to fetch audit log details:', err);
      setError(err.response?.data?.message || 'Failed to load audit log details');
    } finally {
      setLoadingDetail(false);
    }
  };

  // Export to CSV
  const handleExportCSV = async () => {
    try {
      setExporting(true);

      // Build filter body
      const filters: any = {};

      if (searchTerm) {
        filters.user_id = searchTerm;
      }

      if (selectedEventTypes.length > 0) {
        filters.event_type = selectedEventTypes[0];
      }

      if (selectedResults.length > 0) {
        filters.result = selectedResults[0];
      }

      if (dateRange[0]) {
        filters.start_date = dateRange[0].toISOString();
      }

      if (dateRange[1]) {
        filters.end_date = dateRange[1].toISOString();
      }

      const response = await axios.post(`${API_BASE_URL}/v1/admin/rbac/audit-logs/export`, filters, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.data.success && response.data.data.csv_content) {
        // Create a blob and download
        const blob = new Blob([response.data.data.csv_content], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `audit-logs-${Date.now()}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (err: any) {
      console.error('Failed to export audit logs:', err);
      setError(err.response?.data?.message || 'Failed to export audit logs');
    } finally {
      setExporting(false);
    }
  };

  // BUGFIX: Do NOT auto-fetch on mount - users should use search/filter controls
  // Auto-fetching causes page to stuck in loading state if API fails
  // Previous code immediately called fetchAuditLogs() which set loading=true
  // If API call failed or timed out, loading state never cleared, preventing UI from rendering
  //
  // useEffect(() => {
  //   fetchAuditLogs();
  // }, [fetchAuditLogs]);

  // Format timestamp
  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  // Table headers
  const headers = [
    { key: 'user_email', header: 'User' },
    { key: 'event_type', header: 'Event Type' },
    { key: 'action', header: 'Action' },
    { key: 'resource_type', header: 'Resource' },
    { key: 'result', header: 'Result' },
    { key: 'timestamp', header: 'Timestamp' },
    { key: 'actions', header: 'Actions' },
  ];

  // Prepare table rows
  const rows = logs.map(log => ({
    id: log.audit_id.toString(),
    user_email: log.user_email,
    event_type: log.event_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    action: log.action,
    resource_type: log.resource_type,
    result: log.result,
    timestamp: formatTimestamp(log.timestamp),
    audit_id: log.audit_id,
  }));

  if (loading && logs.length === 0) {
    return (
      <div className={styles.loadingContainer}>
        <Loading description="Loading audit logs..." withOverlay={false} />
      </div>
    );
  }

  return (
    <Grid fullWidth className={styles.auditLogDashboard}>
      {/* Header Section */}
      <Column lg={16}>
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>Audit Log Dashboard</h1>
          <p className={styles.pageDescription}>View comprehensive audit logs of permission checks and role changes</p>
        </div>
      </Column>

      {/* Error notification */}
      {error && (
        <Column lg={16}>
          <InlineNotification
            kind="error"
            title="Error"
            subtitle={error}
            onCloseButtonClick={() => setError(null)}
            className={styles.errorNotification}
          />
        </Column>
      )}

      {/* Filters Section */}
      <Column lg={16}>
        <Tile className={styles.filterTile}>
          <Grid>
            <Column lg={4} md={4} sm={4}>
              <DatePicker
                datePickerType="range"
                onChange={(dates: Date[]) => setDateRange([dates[0] || null, dates[1] || null])}
              >
                <DatePickerInput
                  id="date-picker-input-start"
                  placeholder="mm/dd/yyyy"
                  labelText="Start Date"
                  size="md"
                />
                <DatePickerInput id="date-picker-input-end" placeholder="mm/dd/yyyy" labelText="End Date" size="md" />
              </DatePicker>
            </Column>

            <Column lg={4} md={4} sm={4}>
              <MultiSelect
                id="event-type-filter"
                titleText="Event Type"
                label="Select event types"
                items={eventTypeOptions}
                itemToString={item => (item ? item.label : '')}
                selectedItems={eventTypeOptions.filter(opt => selectedEventTypes.includes(opt.id))}
                onChange={({ selectedItems }) => {
                  setSelectedEventTypes(selectedItems.map(item => item.id));
                }}
              />
            </Column>

            <Column lg={4} md={4} sm={4}>
              <MultiSelect
                id="result-filter"
                titleText="Result"
                label="Select results"
                items={resultOptions}
                itemToString={item => (item ? item.label : '')}
                selectedItems={resultOptions.filter(opt => selectedResults.includes(opt.id))}
                onChange={({ selectedItems }) => {
                  setSelectedResults(selectedItems.map(item => item.id));
                }}
              />
            </Column>

            <Column lg={4} md={4} sm={4}>
              <div className={styles.filterButtonContainer}>
                <Button
                  kind="primary"
                  renderIcon={Search}
                  onClick={() => {
                    setCurrentPage(1);
                    fetchAuditLogs();
                  }}
                >
                  Apply Filters
                </Button>
                <Button
                  kind="tertiary"
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedEventTypes([]);
                    setSelectedResults([]);
                    setDateRange([null, null]);
                    setCurrentPage(1);
                  }}
                >
                  Clear
                </Button>
              </div>
            </Column>
          </Grid>
        </Tile>
      </Column>

      {/* Data Table */}
      <Column lg={16}>
        <DataTable rows={rows} headers={headers} isSortable>
          {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
            <TableContainer title="Audit Logs" description={`Showing ${logs.length} of ${totalLogs} total logs`}>
              <TableToolbar>
                <TableToolbarContent>
                  <TableToolbarSearch
                    persistent
                    placeholder="Search by user ID or email"
                    onChange={(e: any) => setSearchTerm(e.target.value)}
                    onClear={() => setSearchTerm('')}
                  />
                  <Button
                    kind="primary"
                    renderIcon={Download}
                    onClick={handleExportCSV}
                    disabled={exporting || logs.length === 0}
                  >
                    {exporting ? 'Exporting...' : 'Export CSV'}
                  </Button>
                </TableToolbarContent>
              </TableToolbar>
              <Table {...getTableProps()}>
                <TableHead>
                  <TableRow>
                    {headers.map(header => (
                      <TableHeader {...getHeaderProps({ header })} key={header.key}>
                        {header.header}
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map(row => (
                    <TableRow {...getRowProps({ row })} key={row.id}>
                      {row.cells.map(cell => {
                        if (cell.info.header === 'result') {
                          return (
                            <TableCell key={cell.id}>
                              <Tag type={cell.value === 'granted' ? 'green' : 'red'} size="sm">
                                {cell.value}
                              </Tag>
                            </TableCell>
                          );
                        }
                        if (cell.info.header === 'actions') {
                          return (
                            <TableCell key={cell.id}>
                              <Button
                                kind="ghost"
                                size="sm"
                                renderIcon={View}
                                onClick={() =>
                                  fetchAuditLogDetail(
                                    row.cells.find(c => c.info.header === 'user_email')?.value ? parseInt(row.id) : 0
                                  )
                                }
                                disabled={loadingDetail}
                              >
                                View
                              </Button>
                            </TableCell>
                          );
                        }
                        return <TableCell key={cell.id}>{cell.value}</TableCell>;
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DataTable>
      </Column>

      {/* Pagination */}
      <Column lg={16}>
        <Pagination
          totalItems={totalLogs}
          pageSize={pageSize}
          pageSizes={[20, 50, 100]}
          page={currentPage}
          onChange={({ page, pageSize: newPageSize }) => {
            setCurrentPage(page);
            if (newPageSize !== pageSize) {
              setPageSize(newPageSize);
              setCurrentPage(1);
            }
          }}
        />
      </Column>

      {/* Detail Modal */}
      <Modal
        open={isDetailModalOpen}
        onRequestClose={() => {
          setIsDetailModalOpen(false);
          setSelectedLog(null);
        }}
        modalHeading="Audit Log Details"
        primaryButtonText="Close"
        onRequestSubmit={() => {
          setIsDetailModalOpen(false);
          setSelectedLog(null);
        }}
        size="lg"
      >
        {selectedLog && (
          <div>
            <Grid>
              <Column lg={8} md={4} sm={4}>
                <div className={styles.detailSection}>
                  <h5 className={styles.detailHeader}>User</h5>
                  <p className={styles.detailValue}>
                    {selectedLog.user.first_name} {selectedLog.user.last_name} ({selectedLog.user.email})
                  </p>
                  <p className={styles.detailSubtext}>Role: {selectedLog.user.role}</p>
                </div>
              </Column>

              <Column lg={8} md={4} sm={4}>
                <div className={styles.detailSection}>
                  <h5 className={styles.detailHeader}>Resource</h5>
                  <p className={styles.detailValue}>{selectedLog.resource.type}</p>
                  {selectedLog.resource.id && <p className={styles.detailSubtext}>ID: {selectedLog.resource.id}</p>}
                </div>
              </Column>

              <Column lg={8} md={4} sm={4}>
                <div className={styles.detailSection}>
                  <h5 className={styles.detailHeader}>Action</h5>
                  <p className={styles.detailValue}>{selectedLog.action}</p>
                </div>
              </Column>

              <Column lg={8} md={4} sm={4}>
                <div className={styles.detailSection}>
                  <h5 className={styles.detailHeader}>Decision</h5>
                  <Tag type={selectedLog.decision.result === 'granted' ? 'green' : 'red'}>
                    {selectedLog.decision.result}
                  </Tag>
                </div>
              </Column>

              {selectedLog.decision.basis && (
                <Column lg={16}>
                  <div className={styles.detailSection}>
                    <h5 className={styles.detailHeader}>Decision Basis</h5>
                    <pre className={styles.detailCode}>{JSON.stringify(selectedLog.decision.basis, null, 2)}</pre>
                  </div>
                </Column>
              )}

              <Column lg={8} md={4} sm={4}>
                <div className={styles.detailSection}>
                  <h5 className={styles.detailHeader}>IP Address</h5>
                  <p className={styles.detailSubtext}>{selectedLog.metadata.ip_address || 'N/A'}</p>
                </div>
              </Column>

              <Column lg={8} md={4} sm={4}>
                <div className={styles.detailSection}>
                  <h5 className={styles.detailHeader}>Timestamp</h5>
                  <p className={styles.detailSubtext}>{formatTimestamp(selectedLog.metadata.timestamp)}</p>
                </div>
              </Column>

              {selectedLog.metadata.user_agent && (
                <Column lg={16}>
                  <div className={styles.detailSection}>
                    <h5 className={styles.detailHeader}>User Agent</h5>
                    <p className={`${styles.detailSubtext} ${styles.wordBreakAll}`}>
                      {selectedLog.metadata.user_agent}
                    </p>
                  </div>
                </Column>
              )}
            </Grid>
          </div>
        )}
      </Modal>
    </Grid>
  );
};

export default AuditLogDashboard;
